/**
 * FilterBar - Horizontal filter bar with date range and multi-select dropdowns
 *
 * Supports:
 * - Date range picker
 * - Single-select dropdowns
 * - Multi-select dropdowns with chip display
 * - Active filter chips with removal
 */

import { useState, useRef, useEffect } from "react";

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
}

interface FilterBarProps {
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  filters?: FilterConfig[];
  onClearAll?: () => void;
  showDateFieldToggle?: boolean;
  dateField?: "timestamp" | "dateCallWasTaken";
  onDateFieldChange?: (field: "timestamp" | "dateCallWasTaken") => void;
}

// Multi-select dropdown component with search and Apply button
function MultiSelectDropdown({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Local state for pending selections (only applied when user clicks Apply)
  const [pendingSelection, setPendingSelection] = useState<string[]>(value);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync pending selection when value changes externally (e.g., clear all)
  useEffect(() => {
    setPendingSelection(value);
  }, [value]);

  // Close dropdown when clicking outside (discard pending changes)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
        setPendingSelection(value); // Reset to current applied value
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const toggleOption = (optionValue: string) => {
    if (pendingSelection.includes(optionValue)) {
      setPendingSelection(pendingSelection.filter((v) => v !== optionValue));
    } else {
      setPendingSelection([...pendingSelection, optionValue]);
    }
  };

  const handleApply = () => {
    onChange(pendingSelection);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = () => {
    setPendingSelection([]);
  };

  // Filter options based on search query (defensive: ensure label is a string)
  const filteredOptions = options.filter((option) => {
    const label = typeof option.label === 'string' ? option.label : String(option.label || option.value || '');
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Get display label for a selected value (defensive: ensure it's a string)
  const getDisplayLabel = (val: string): string => {
    const option = options.find((o) => o.value === val);
    if (!option) return val;
    return typeof option.label === 'string' ? option.label : String(option.label || val);
  };

  // Display text shows applied value count, not pending
  const displayText = value.length === 0 ? label : value.length === 1
    ? (() => {
        const firstValue = value[0];
        if (!firstValue) return label;
        const displayLabel = getDisplayLabel(firstValue);
        return displayLabel.length > 20 ? displayLabel.substring(0, 20) + "…" : displayLabel;
      })()
    : `${value.length} selected`;

  // Check if pending selection differs from applied value
  const hasChanges = JSON.stringify([...pendingSelection].sort()) !== JSON.stringify([...value].sort());

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 md:gap-2 h-8 md:h-9 px-2 md:px-3 rounded-md border text-xs md:text-sm min-w-[110px] md:min-w-[160px] max-w-[140px] md:max-w-[200px] transition-colors ${
          value.length > 0
            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
            : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
        }`}
      >
        <span className="truncate flex-1 text-left">{displayText}</span>
        <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-[100] bg-popover border border-border rounded-md shadow-lg min-w-[200px] sm:min-w-[300px] max-w-[calc(100vw-2rem)] sm:max-w-[450px]">
          {/* Search input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-8 pl-8 pr-3 text-sm border border-border rounded bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-[250px] overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No options found</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = pendingSelection.includes(option.value);
                const displayLabel = typeof option.label === 'string' ? option.label : String(option.label || option.value || '');
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors ${
                      isSelected
                        ? "bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                        : "hover:bg-accent"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOption(option.value)}
                      className="rounded border-border accent-[var(--brand-primary)]"
                    />
                    <span className="truncate">{displayLabel}</span>
                  </label>
                );
              })
            )}
          </div>

          {/* Footer with count, clear, and apply buttons */}
          <div className="p-2 border-t border-border flex justify-between items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {pendingSelection.length} selected
              {hasChanges && <span className="text-[var(--brand-primary)]"> *</span>}
            </span>
            <div className="flex items-center gap-2">
              {pendingSelection.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={handleApply}
                className="px-3 py-1 text-xs bg-[var(--brand-primary)] text-white rounded hover:opacity-90 transition-opacity"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function FilterBar({
  dateRange,
  onDateRangeChange,
  filters = [],
  onClearAll,
  showDateFieldToggle = false,
  dateField = "timestamp",
  onDateFieldChange,
}: FilterBarProps) {
  const [isDateOpen, setIsDateOpen] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);

  // Close date dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dateRef.current && !dateRef.current.contains(event.target as Node)) {
        setIsDateOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasActiveFilters =
    Boolean(dateRange?.from || dateRange?.to) ||
    filters.some((f) => (f.multiple ? (f.value as string[]).length > 0 : f.value !== ""));
  const visibleFilters = filters.filter((filter) => filter.options.length > 0);

  const formatDateRange = () => {
    if (!dateRange?.from) return "Select dates";
    const fromStr = dateRange.from.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (!dateRange.to) return fromStr;
    const toStr = dateRange.to.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fromStr} - ${toStr}`;
  };

  // Collect all active filter chips for display
  const activeChips: { key: string; value: string; label: string }[] = [];
  filters.forEach((filter) => {
    if (filter.multiple && Array.isArray(filter.value)) {
      filter.value.forEach((v) => {
        const option = filter.options.find((o) => o.value === v);
        activeChips.push({
          key: filter.key,
          value: v,
          label: option?.label || v,
        });
      });
    }
  });

  const removeChip = (key: string, value: string) => {
    const filter = filters.find((f) => f.key === key);
    if (filter && filter.multiple && Array.isArray(filter.value)) {
      filter.onChange(filter.value.filter((v) => v !== value));
    }
  };

  return (
    <div className="relative z-10 w-full rounded-xl border border-border/50 bg-card/50 p-3 md:p-4 backdrop-blur-sm">
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        {/* Date Range Picker */}
        {onDateRangeChange && (
          <div ref={dateRef} className="relative">
            <button
              type="button"
              onClick={() => setIsDateOpen(!isDateOpen)}
              className={`flex items-center gap-2 h-9 px-3 rounded-md border text-sm transition-colors ${
                dateRange?.from
                  ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                  : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formatDateRange()}</span>
              {dateRange?.from && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDateRangeChange({ from: undefined, to: undefined });
                  }}
                  className="ml-1 hover:text-destructive"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </button>
            {isDateOpen && (
              <div className="absolute top-full left-0 mt-1 z-[100] bg-popover border border-border rounded-md shadow-lg p-4">
                <div className="flex gap-2 mb-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">From</label>
                    <input
                      type="date"
                      className="px-2 py-1 border border-input rounded text-sm bg-background"
                      value={dateRange?.from?.toISOString().split("T")[0] || ""}
                      onChange={(e) => {
                        const from = e.target.value ? new Date(e.target.value + "T00:00:00") : undefined;
                        onDateRangeChange({ from, to: dateRange?.to });
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">To</label>
                    <input
                      type="date"
                      className="px-2 py-1 border border-input rounded text-sm bg-background"
                      value={dateRange?.to?.toISOString().split("T")[0] || ""}
                      onChange={(e) => {
                        const to = e.target.value ? new Date(e.target.value + "T00:00:00") : undefined;
                        onDateRangeChange({ from: dateRange?.from, to });
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      onDateRangeChange({ from: undefined, to: undefined });
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1 text-xs bg-[var(--brand-primary)] text-white rounded hover:opacity-90"
                    onClick={() => setIsDateOpen(false)}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Date Field Toggle */}
        {showDateFieldToggle && onDateFieldChange && (
          <select
            className="h-8 md:h-9 px-2 md:px-3 rounded-md border border-input bg-background text-xs md:text-sm"
            value={dateField}
            onChange={(e) => onDateFieldChange(e.target.value as "timestamp" | "dateCallWasTaken")}
          >
            <option value="dateCallWasTaken">Date Call Was Taken</option>
            <option value="timestamp">Timestamp</option>
          </select>
        )}

        {/* Dynamic Filters */}
        {visibleFilters.map((filter) =>
          filter.multiple ? (
            <MultiSelectDropdown
              key={filter.key}
              label={filter.label}
              options={filter.options}
              value={filter.value as string[]}
              onChange={(v) => filter.onChange(v)}
            />
          ) : (
            <select
              key={filter.key}
              className={`h-8 md:h-9 px-2 md:px-3 rounded-md border text-xs md:text-sm min-w-[100px] md:min-w-[140px] transition-colors ${
                filter.value
                  ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
                  : "border-input bg-background"
              }`}
              value={filter.value as string}
              onChange={(e) => filter.onChange(e.target.value)}
            >
              <option value="">{filter.label}</option>
              {filter.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )
        )}

        {/* Active Filter Chips */}
        {activeChips.length > 0 && (
          <div className="hidden sm:flex flex-wrap items-center gap-1.5 md:gap-2 ml-1 md:ml-2">
            {activeChips.slice(0, 3).map((chip) => (
              <span
                key={`${chip.key}-${chip.value}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--brand-primary)]/20 text-[var(--brand-primary)] text-xs font-medium"
              >
                {chip.label}
                <button
                  type="button"
                  onClick={() => removeChip(chip.key, chip.value)}
                  className="hover:text-destructive"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
            {activeChips.length > 3 && (
              <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs">
                +{activeChips.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Clear All Button */}
      {hasActiveFilters && onClearAll && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={onClearAll}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}
